// --------------------------------------------------------------
// Created On: 2023-02-09
// Author: Zachary Thomas
//
// Last Modified: 2026-02-11
// Modified By: Hannah Vaughan
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useMemo } from "react";
import styles from "./ReportAttributeForm.module.scss";
import { EQUAL_TO_COMPARATOR_NAME, NOT_EQUAL_TO_COMPARATOR_NAME } from "src/constants/miscellaneous";

// Form for assigning attributes to a report.
export default function ReportAttributeForm(props: Props): Component {
  const isAttributeBoolean = useMemo(
    () => attributeIsBoolean(props.name, props.attributes),
    [props.name, props.attributes]
  );
  const isAttributeEnumeration = useMemo(
    () => attributeIsEnumeration(props.name, props.attributes),
    [props.name, props.attributes]
  );
  const attributeUnitName = useMemo(
    () => getAttributeUnitName(props.name, props.attributes),
    [props.name, props.attributes]
  );
  const enumerationOptions = useMemo(
    () => getEnumerationOptionsForAttribute(props.name, props.attributes, props.enumerationMap),
    [props.name, props.attributes, props.enumerationMap]
  );

  // Gets the attribute unit name using the attribute ID.
  function getAttributeUnitName(attributeName: string, attributes: Attribute[]): string {
    const attribute = attributes.find((attribute) => attribute.name === attributeName);
    if (
      attribute !== undefined &&
      attribute !== null &&
      attribute.unitShortName !== undefined &&
      attribute.unitShortName !== null
    ) {
      return attribute.unitShortName;
    } else {
      return "";
    }
  }

  // Checks if the specified attribute describes a boolean value.
  function attributeIsBoolean(attributeName: string, attributes: Attribute[]): boolean {
    const attribute = attributes.find((attribute) => attribute.name.toLowerCase() === attributeName.toLowerCase());
    if (attribute !== undefined && attribute !== null) {
      return attribute.isBoolean;
    } else {
      return false;
    }
  }

  // Checks if the specified attribute describes an enumeration value.
  function attributeIsEnumeration(attributeName: string, attributes: Attribute[]): boolean {
    const attribute = attributes.find((attribute) => attribute.name.toLowerCase() === attributeName.toLowerCase());
    if (
      attribute !== undefined &&
      attribute !== null &&
      attribute.regEnumId !== undefined &&
      attribute.regEnumId !== null
    ) {
      return true;
    } else {
      return false;
    }
  }

  // Gets an array of enumeration options for a specific attribute.
  function getEnumerationOptionsForAttribute(
    attributeName: string,
    attributes: Attribute[],
    enumerationMap: EnumerationMap
  ): EnumerationOption[] {
    const enumerationOptions: EnumerationOption[] = [];
    const attribute = attributes.find((attribute) => attribute.name.toLowerCase() === attributeName.toLowerCase());
    if (
      attribute !== undefined &&
      attribute !== null &&
      attribute.regEnumId !== undefined &&
      attribute.regEnumId !== null &&
      enumerationMap[attribute.regEnumId] !== undefined
    ) {
      const enumerationOptionIndexes = Object.keys(enumerationMap[attribute.regEnumId]);
      enumerationOptionIndexes.forEach((enumerationOptionIndex) => {
        if (enumerationMap[attribute.regEnumId as number][enumerationOptionIndex] !== undefined) {
          enumerationOptions.push({
            index: parseInt(enumerationOptionIndex, 10),
            name: enumerationMap[attribute.regEnumId as number][enumerationOptionIndex],
          });
        }
      });
    }
    return enumerationOptions;
  }

  // Get the ID of a comparator by name.
  function getComparatorIdByName(name: string, comparators: Comparator[]): number {
    const foundComparator = comparators.find((comparator) => comparator.name.toLowerCase() === name.toLowerCase());
    if (foundComparator !== undefined) {
      return foundComparator.alertThresholdComparatorId;
    } else {
      return 0;
    }
  }

  // Sets the threshold comparator and value to default values when the attribute is updated.
  // Note that all boolean attributes use the "equal to" comparator.
  function updateThresholdAttribute(attributeName: string, attributes: Attribute[], comparators: Comparator[]): void {
    // The function has to be called here instead of the memoized value because the value doesn't update until after this function completes.
    if (attributeIsBoolean(attributeName, attributes)) {
      props.onChange(props.formId, attributeName, getComparatorIdByName("equal to", comparators), "");
    } else {
      props.onChange(props.formId, attributeName, 0, "");
    }
  }

  return (
    <div className="my-3 mx-1">
      <div className="row align-items-center">
        <div className={`${!props.allowsComparators ? "col-4 col-lg-3 col-xl-2" : "d-none"}`}>
          <label className={styles.title}>Attribute #{props.formIndex + 1}:</label>
        </div>
        <div
          className={`${
            props.allowsComparators
              ? isAttributeBoolean
                ? "col-12 col-lg-6"
                : "col-12 col-lg-4"
              : "col-5 col-sm-6 col-lg-8 col-xl-9"
          }`}
        >
          <label className={`${styles.title} ${!props.allowsComparators && "d-none"}`}>
            Attribute #{props.formIndex + 1}:
          </label>
          <input
            data-test="report-attribute-form-input"
            className="form-control mx-auto"
            type="text"
            list="datalist-attributes"
            autoComplete="off"
            value={props.name}
            disabled={props.disabled}
            onChange={(e) => updateThresholdAttribute(e.target.value, props.attributes, props.comparators)}
          />
        </div>

        {props.allowsComparators && isAttributeBoolean && (
          <div className="col-10 col-lg-5 col-auto">
            <label className={styles.title}>Is Value</label>
            <select
              className="form-select mx-auto"
              value={props.value}
              disabled={props.disabled}
              onChange={(e) =>
                props.onChange(
                  props.formId,
                  props.name,
                  getComparatorIdByName("equal to", props.comparators),
                  e.target.value
                )
              }
            >
              <option value="" disabled>
                Please select a value...
              </option>
              <option value="1">True</option>
              <option value="0">False</option>
            </select>
          </div>
        )}

        {props.allowsComparators && isAttributeEnumeration && (
          <Fragment>
            <div className="col-12 col-lg-3">
              <label className={styles.title}>Comparator</label>
              <select
                className="form-select col-12"
                value={props.comparatorId}
                disabled={props.disabled}
                onChange={(e) => props.onChange(props.formId, props.name, parseInt(e.target.value, 10), props.value)}
              >
                <option value={0} disabled>
                  Please select a comparator...
                </option>
                {props.comparators !== undefined &&
                  props.comparators !== null &&
                  props.comparators.map(
                    (comparator) =>
                      [EQUAL_TO_COMPARATOR_NAME, NOT_EQUAL_TO_COMPARATOR_NAME].includes(comparator.name) && (
                        <option
                          key={comparator.alertThresholdComparatorId}
                          value={comparator.alertThresholdComparatorId}
                        >
                          {comparator.name}
                        </option>
                      )
                  )}
              </select>
            </div>

            <div className="col-10 col-lg-4">
              <label className={`${styles.title} ${attributeUnitName ? "w-75" : "w-100"}`}>Value</label>
              <select
                className="form-select col-12"
                value={props.value}
                disabled={props.disabled}
                onChange={(e) => props.onChange(props.formId, props.name, props.comparatorId, e.target.value)}
              >
                <option value="" disabled>
                  Please select a value...
                </option>
                {enumerationOptions.map((option) => (
                  <option key={option.index} value={option.index}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>
          </Fragment>
        )}

        {props.allowsComparators && !isAttributeBoolean && !isAttributeEnumeration && (
          <Fragment>
            <div className="col-12 col-lg-3">
              <label className={styles.title}>Comparator</label>
              <select
                className="form-select col-12"
                value={props.comparatorId}
                disabled={props.disabled}
                onChange={(e) => props.onChange(props.formId, props.name, parseInt(e.target.value, 10), props.value)}
              >
                <option value={0} disabled>
                  Please select a comparator...
                </option>
                {props.comparators !== undefined &&
                  props.comparators !== null &&
                  props.comparators.map((comparator) => (
                    <option key={comparator.alertThresholdComparatorId} value={comparator.alertThresholdComparatorId}>
                      {comparator.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="col-10 col-lg-4">
              <label className={attributeUnitName.length > 0 ? "w-75" : "w-100"}>Value</label>
              <input
                className={`form-control mx-auto d-inline-block ${attributeUnitName.length > 0 ? "w-75" : "w-100"}`}
                type="text"
                value={props.value}
                disabled={props.disabled}
                onChange={(e) => props.onChange(props.formId, props.name, props.comparatorId, e.target.value)}
              />
              <span className="mx-2">{attributeUnitName}</span>
            </div>
          </Fragment>
        )}

        <div className={`${props.allowsComparators ? "col-2 mt-4" : "col-3"} col-sm-2 col-lg-1`}>
          <button
            type="button"
            className={`${styles.button} btn btn-danger float-end`}
            disabled={props.disabled}
            onClick={() => props.onDelete(props.formId)}
          >
            <i className="fa fa-fw fa-times fa-xs" />
          </button>
        </div>
      </div>
    </div>
  );
}

interface Props {
  formIndex: number;
  formId: number;
  name: string;
  comparatorId?: number;
  value?: string;
  attributes: Attribute[];
  comparators: Comparator[];
  enumerationMap: EnumerationMap;
  disabled: boolean;
  allowsComparators: boolean;
  onChange: (formId: number, name: string, comparatorId?: number, value?: string) => void;
  onDelete: (formId: number) => void;
}

interface Attribute {
  attributeId: number;
  name: string;
  isBoolean: boolean;
  unitShortName?: string;
  regEnumId?: number;
}

interface Comparator {
  alertThresholdComparatorId: number;
  name: string;
}

interface EnumerationMap {
  [key: string]: EnumerationIndexMap;
}

interface EnumerationIndexMap {
  [key: string]: string;
}

interface EnumerationOption {
  index: number;
  name: string;
}
